import { useQuery, gql } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import { ZB_MONEY_CONTEXT_DEFAULTS } from './constants';

export const HOME_QUERY = gql`
  query HomeQuery($businessEntityUuid: UUID!, $journeySlug: String!) {
    businessEntity: businessEntityLegacy(businessEntityUuid: $businessEntityUuid) {
      businessEntityUuid
      businessEntityName
      businessEntityType
      createdDatetime
      businessEntityData {
        contact {
          companyName
        }
        domesticJurisdiction
        ein
      }
    }
    accountInfo {
      firstName
      accountUuid
      customFlow
      allPartnerOptOut
      pricing(businessEntityUuid: $businessEntityUuid) {
        service
        amount
        interval
      }
    }
    orderTasksLegacy(businessEntityUuid: $businessEntityUuid) {
      orderTasksLegacy {
        createdDatetime
        orderTaskUuid
        orderTaskStatus
        orderTaskType
        orderTaskSubStatus
        orderTaskStatusUpdates {
          statusUpdatedFrom
          statusUpdatedTo
          subStatusUpdatedTo
          subStatusUpdatedFrom
          createdDatetime
        }
        statusText
        termEndDate
        orderTaskData {
          ... on FutureFileData {
            futureFilingDate
          }
          ... on LLCFormationData {
            estimatedFormationDate
          }
          ... on CorpFormationData {
            estimatedFormationDate
          }
          ... on EntityNameData {
            name
            jurisdiction
          }
          ... on DomainNameData {
            skip
          }
          ... on EINData {
            gambling
            atf
            filesForm720
          }
        }
      }
    }
    journeyRedirect(businessEntityUuid: $businessEntityUuid, journeySlug: $journeySlug) {
        shouldRedirect
        redirectUrl
    } 
    partnerRequestBankingUuid(businessEntityUuid: $businessEntityUuid)
    businessEntities: businessEntitiesLegacy {
      count
      businessEntities {
        businessEntityName
      }
    }
  }
`;

export default function useHomeQuery({
  businessEntityUuid,
  initialStatus = ['incomplete'],
  journeySlug = 'bof_fulfillment_v3',
  purchaseStatus = ['pending', 'complete'],
  pendingStatus = ['pending'],
  reportType = 'annual_report',
  timezone
}) {
  const captureException = useCaptureException();

  const {
    loading,
    errors,
    data = {}
  } = useQuery(HOME_QUERY, {
    skip: !businessEntityUuid,
    variables: {
      businessEntityUuid,
      initialStatus,
      purchaseStatus,
      pendingStatus,
      eventType: reportType,
      timezone,
      journeySlug
    },
    noThrow: true
  });

  if (errors?.length) {
    // Allow partial homeQuery data
    if (data && data.accountInfo !== undefined) {
      captureException(errors[0], { tags: { operationName: 'homeQuery' } });
    } else {
      throw errors[0];
    }
  }
  const {
    accountInfo: {
      pricing = [],
      zbMoneySubscription: {
        accountTier = ZB_MONEY_CONTEXT_DEFAULTS.accountTier,
        isPlaidLinked = ZB_MONEY_CONTEXT_DEFAULTS.isPlaidLinked
      } = {}
    } = {},
    accountInfo,
    businessEntity,
    orderTasksLegacy: { orderTasksLegacy = [] } = {},
    partnerRequestBankingUuid = null,
    businessEntities: { count: businessEntityCount = 0, businessEntities } = {},
    bankApplication: { status: bankApplicationStatus = '' } = {},
    journeyRedirect
  } = data || {};

  return {
    accountInfo,
    accountTier,
    bankApplicationStatus,
    businessEntity,
    businessEntities,
    businessEntityCount,
    journeyRedirect,
    isPlaidLinked,
    loading,
    orderTasks: orderTasksLegacy,
    partnerRequestBankingUuid,
    pricing
  };
}
